import {
  DataButton,
  H5,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import { capitalize } from "@sellar-io/common";
import type {
  DeliveryMethodContainerRuleUnitType,
  DeliveryMethodContainerRulesContainerTypesType,
  FulfillmentTypeListResponseType,
} from "@sellar-io/api-v2/types";
import { Icon, SpinnerLegacy } from "../../../components";
import * as ROUTES from "../../../constants/routes";
import { TableInfinite } from "../../../components/table/TableInfinite";
import { useDeliveryMethodContainerRulesContainerTypes } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesContainerTypes";
import { useDeliveryMethodContainerRulesCreate } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesCreate";
import { useDeliveryMethodContainerRulesDelete } from "../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesDelete";
import { twMerge } from "tailwind-merge";

export const DeliveryMethodContainerRulesOverview = ({
  deliveryMethods,
  isDeliveryMethodsLoading,
  fetchNextPage,
  isFetching,
  onBack,
}: {
  deliveryMethods: FulfillmentTypeListResponseType;
  isDeliveryMethodsLoading: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  onBack: () => void;
}) => {
  const navigate = useNavigate();

  const containerTypesQuery = useDeliveryMethodContainerRulesContainerTypes({
    enabled: true,
  });

  const containerTypes = useMemo(
    () => containerTypesQuery.data ?? [],
    [containerTypesQuery.data],
  );

  const createRuleMutation = useDeliveryMethodContainerRulesCreate();
  const deleteRuleMutation = useDeliveryMethodContainerRulesDelete();

  const isSaving = createRuleMutation.isPending || deleteRuleMutation.isPending;

  const tableData = deliveryMethods;

  const columnHelper = useMemo(
    () => createSellarColumnHelper<FulfillmentTypeListResponseType[number]>(),
    [],
  );

  const columns = useMemo(() => {
    const deliveryMethodColumn = columnHelper.accessor((row) => row, {
      id: "delivery_method",
      header: "",
      enableSorting: false,
      cell: (info) => {
        const method = info.getValue();

        return (
          <button
            type="button"
            className="group flex w-full cursor-pointer items-center justify-between gap-2 overflow-hidden p-2"
            onClick={() =>
              navigate(
                `${ROUTES.DELIVERY_METHOD.href({ deliveryMethodId: method.id })}?view=container-rules`,
              )
            }
          >
            <span className="w-full overflow-hidden text-left font-medium text-nowrap text-ellipsis">
              {method.name ?? `${capitalize(method.fulfillmentType)} delivery`}
            </span>
            <Icon
              icon="CHEVRON_RIGHT"
              size={0.7}
              className="flex-none text-zinc-400 group-hover:text-zinc-700"
            />
          </button>
        );
      },
    });

    const containerColumns = (
      containerTypes as DeliveryMethodContainerRulesContainerTypesType
    ).map((container) => {
      const key = `${container.containerType}-${container.volume}-${container.displayUnit}`;
      const headerLabel = `${container.volume}${container.displayUnit} ${container.displayName}`;

      return columnHelper.accessor((row) => row, {
        id: key,
        header: () => (
          <span className="block w-full overflow-hidden px-2 py-1 text-xs font-medium text-nowrap text-ellipsis">
            {headerLabel}
          </span>
        ),
        enableSorting: false,
        cell: (info) => {
          const method = info.getValue();
          const existingRule = method.DeliveryMethodContainerRules.find(
            (rule) =>
              `${rule.containerType}-${rule.volume}-${rule.unit}` === key,
          );
          const isAvailable = !existingRule;
          const existingRuleId = existingRule?.id;

          return (
            <div className="flex h-full w-full items-center justify-center p-2">
              <input
                type="checkbox"
                className={twMerge(
                  "cursor-pointer",
                  isSaving && "pointer-events-none",
                )}
                checked={isAvailable}
                onChange={(e) => {
                  const nextChecked = e.target.checked;

                  if (!nextChecked) {
                    if (existingRuleId) return;

                    createRuleMutation.mutate({
                      containerType: container.containerType,
                      unit: container.displayUnit as DeliveryMethodContainerRuleUnitType,
                      volume: container.volume,
                      FulfillmentTypeId: method.id,
                      SupplierId: null,
                      systemDefinedType: null,
                    });

                    return;
                  }

                  if (!existingRuleId) return;

                  deleteRuleMutation.mutate(existingRuleId);
                }}
              />
            </div>
          );
        },
      });
    });

    return [deliveryMethodColumn, ...containerColumns];
  }, [
    columnHelper,
    containerTypes,
    createRuleMutation,
    deleteRuleMutation,
    isSaving,
    navigate,
  ]);

  const gridClasses = useMemo(() => {
    if (containerTypes.length === 0) return "grid-cols-[minmax(220px,_1fr)]";
    return `grid-cols-[minmax(220px,_1fr)_repeat(${containerTypes.length},_minmax(140px,_1fr))]`;
  }, [containerTypes.length]);

  const gridTemplateColumns = useMemo(() => {
    if (containerTypes.length === 0) return "minmax(220px, 1fr)";
    return `minmax(220px, 1fr) repeat(${containerTypes.length}, minmax(140px, 1fr))`;
  }, [containerTypes.length]);

  const isLoading = containerTypesQuery.isLoading || isDeliveryMethodsLoading;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <H5>Container rules</H5>
          <span className="text-zinc-500">
            Select which of your listed containers can be shipped via each
            delivery method
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DataButton
            className="flex items-center gap-1"
            border={true}
            onClick={onBack}
          >
            <Icon icon="CHEVRON_LEFT" />
            Back to delivery methods
          </DataButton>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <SpinnerLegacy />
        </div>
      ) : (
        <div className="mb-8">
          <TableInfinite
            columns={columns}
            data={tableData}
            noResultsText="No delivery methods found."
            horizontalInfinite={true}
            roundedTop={true}
            gridClasses={gridClasses}
            gridStyles={{
              gridTemplateColumns,
            }}
            infiniteQuery={{
              data: tableData,
              fetchNextPage,
              isFetching,
            }}
          />
        </div>
      )}
    </div>
  );
};
